/**
 * Your own account, as opposed to a group's settings.
 *
 * These three things existed on the server and had nowhere to be done from:
 * the name other people see, the address the account is reachable at, and the
 * password. Group settings is a different screen about a different thing, and
 * putting personal details there would have meant somebody in two groups
 * editing themselves in one of them, arbitrarily.
 *
 * Each section stands alone and saves alone. A single "Save" across all three
 * would mean changing a display name asks for a password, which is both
 * annoying and misleading about which of them is the sensitive one.
 */

import { useEffect, useState } from "react";
import {
  LOCALES,
  LOCALE_NAMES,
  useLocale,
  useT,
  type Locale,
} from "../../lib/i18n.js";
import { Avatar } from "../../components/ui/Avatar.js";
import { Button } from "../../components/ui/Button.js";
import { openFilePicker } from "../../lib/filePicker.js";
import { toAvatarDataUrl } from "./avatarImage.js";
import { Field } from "../../components/ui/Field.js";
import { Input } from "../../components/ui/Input.js";
import { useToast } from "../../components/ui/Toast.js";
import { useAuth } from "../../lib/auth.js";
import { trpcClient } from "../../lib/trpc.js";
import "./AccountPage.css";
import { TwoFactorSection } from "./TwoFactorSection.js";
import { RecoverySection } from "./RecoverySection.js";
import { PublicKeySection } from "./PublicKeySection.js";
import { ProtectGroups } from "./ProtectGroups.js";

export function AccountPage() {
  const t = useT();
  const { user, refreshUser, signOut } = useAuth();
  const { toast } = useToast();

  return (
    <div className="fc-account">
      <header className="fc-account__header">
        <h1>{t("Your account")}</h1>
        <p>{user?.username}</p>
      </header>

      <AvatarSection
        name={user?.displayName ?? "?"}
        image={user?.avatarImage ?? null}
        onSaved={() => {
          void refreshUser();
          toast(t("Your profile picture has been updated."));
        }}
      />

      <NameSection
        current={user?.displayName ?? ""}
        onSaved={() => {
          void refreshUser();
          toast("Your name has been updated.");
        }}
      />

      <EmailSection
        current={user?.email ?? null}
        verified={user?.emailVerified ?? false}
        onSaved={() => {
          void refreshUser();
          toast("Check the new address for a confirmation link.");
        }}
      />

      <LanguageSection />

      <RecoverySection />

      <PasswordSection
        onSaved={() => toast("Your password has been changed.")}
      />

      {/* Above the key sections, because it is a thing to *do* and they are
          things to know. Renders nothing once every group is covered. */}
      <ProtectGroups />

      {/* Beside two-factor: both are about proving who you are. */}
      <PublicKeySection publicKey={user?.publicKey ?? null} />

      {/* After the password, because it is the second half of the same subject. */}
      <TwoFactorSection
        enabled={user?.totpEnabled ?? false}
        onChanged={() => void refreshUser()}
      />

      {/*
        Signing out lived in the sidebar and nowhere else, and the sidebar is
        `display: none` under 860px — so on a phone there was no way to sign
        out at all. Here it is reachable at every width, and this is the screen
        people look on for it.

        Last on the page, and quiet: it is the one control here that throws
        away what you are in the middle of.
      */}
      <section className="fc-account__section fc-account__signout">
        <div>
          <h2>{t("Sign out")}</h2>
          <p>{t("You will need your password to sign back in.")}</p>
        </div>
        <Button variant="ghost" onClick={() => void signOut()}>
          {t("Sign out")}
        </Button>
      </section>
    </div>
  );
}

/**
 * The profile picture.
 *
 * First on the page, because it is the thing people come to this screen for.
 *
 * It is the one thing here that is *not* encrypted, and the note says so in
 * those words rather than burying it. Every photograph is sealed with a group
 * key, and an avatar has to be the same picture in every group you are in and
 * in the sidebar where there is no group at all — so sealing it per group
 * would mean a copy for each, rewritten whenever you joined another, and it
 * would still be unopenable on the screen that shows it most.
 *
 * The trade is narrow: a profile picture is the one image somebody chooses in
 * order to be recognised by it. Saying so is what makes it a choice.
 */
function AvatarSection({
  name,
  image,
  onSaved,
}: {
  name: string;
  image: string | null;
  onSaved: () => void;
}) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const choose = () => {
    setError(null);
    openFilePicker({
      accept: "image/jpeg,image/png,image/webp,image/heic,image/avif",
      onPick: (files) => {
        const file = files[0];
        if (!file) return;
        void (async () => {
          setBusy(true);
          try {
            const dataUrl = await toAvatarDataUrl(file);
            await trpcClient.auth.setAvatar.mutate({ image: dataUrl });
            onSaved();
          } catch (caught) {
            setError(
              caught instanceof Error
                ? caught.message
                : "That picture could not be used.",
            );
          } finally {
            setBusy(false);
          }
        })();
      },
    });
  };

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await trpcClient.auth.setAvatar.mutate({ image: null });
      onSaved();
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "That could not be removed.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="fc-account__section">
      <h2>{t("Profile picture")}</h2>
      <p className="fc-account__note">
        {t("Shown beside your name everywhere you appear.")}{" "}
        <strong className="fc-account__warn">
          {t(
            "This one picture is not encrypted, unlike your photos — it has to be readable in every group you are in.",
          )}
        </strong>
      </p>

      <div className="fc-account__avatar">
        <Avatar name={name} src={image ?? undefined} size="lg" />
        <div className="fc-account__avatar-actions">
          <Button variant="secondary" loading={busy} onClick={choose}>
            {image ? t("Change picture") : t("Choose a picture")}
          </Button>
          {image && (
            <Button
              variant="ghost"
              disabled={busy}
              onClick={() => void remove()}
            >
              {t("Remove")}
            </Button>
          )}
        </div>
      </div>

      {error && (
        <p className="fc-account__error" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}
function NameSection({
  current,
  onSaved,
}: {
  current: string;
  onSaved: () => void;
}) {
  const t = useT();
  const [displayName, setDisplayName] = useState(current);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Adopt the value once the session has loaded, but never overwrite typing.
  useEffect(() => {
    setDisplayName((value) => (value === "" ? current : value));
  }, [current]);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await trpcClient.auth.updateProfile.mutate({ displayName });
      onSaved();
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "That could not be saved.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="fc-account__section">
      <h2>{t("Name")}</h2>
      <p className="fc-account__note">
        {t("What everyone in your groups sees beside your photos.")}
      </p>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <Field label={t("Display name")} required error={error ?? undefined}>
          <Input
            value={displayName}
            maxLength={60}
            autoComplete="name"
            disabled={busy}
            onChange={(event) => setDisplayName(event.target.value)}
          />
        </Field>
        <Button
          type="submit"
          variant="primary"
          loading={busy}
          disabled={!displayName.trim() || displayName === current}
        >
          {t("Save name")}
        </Button>
      </form>
    </section>
  );
}

function EmailSection({
  current,
  verified,
  onSaved,
}: {
  current: string | null;
  verified: boolean;
  onSaved: () => void;
}) {
  const t = useT();
  const [email, setEmail] = useState(current ?? "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setEmail((value) => (value === "" ? (current ?? "") : value));
  }, [current]);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await trpcClient.auth.changeEmail.mutate({ email, currentPassword });
      setCurrentPassword("");
      onSaved();
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "That could not be saved.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="fc-account__section">
      <h2>{t("Email")}</h2>
      <p className="fc-account__note">
        {t("Used to sign in and to reach you.")}{" "}
        {current && !verified && (
          <strong className="fc-account__warn">
            {t("This address is not confirmed yet.")}
          </strong>
        )}
      </p>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <Field label={t("Email address")} required>
          <Input
            type="email"
            value={email}
            autoComplete="email"
            disabled={busy}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>

        {/*
          The password, because an address is the route back into an account —
          a session on an unattended browser must not be enough to move it.
        */}
        <Field
          label={t("Your password")}
          required
          error={error ?? undefined}
          hint={t(
            "Confirms it is you, because this address can be used to get back in.",
          )}
        >
          <Input
            type="password"
            value={currentPassword}
            autoComplete="current-password"
            disabled={busy}
            onChange={(event) => setCurrentPassword(event.target.value)}
          />
        </Field>

        <Button
          type="submit"
          variant="primary"
          loading={busy}
          disabled={!email.trim() || !currentPassword || email === current}
        >
          {t("Change email")}
        </Button>
      </form>
    </section>
  );
}

/**
 * Language.
 *
 * Sits here rather than in a corner of the header because it is a setting
 * about you, not about the page you happen to be on. It applies immediately —
 * a language choice that needs a reload is a language choice somebody has to
 * trust before they can see it.
 *
 * The options name themselves in their own language. Somebody who has landed
 * in the wrong one cannot read the word for their own language written in a
 * language they do not read.
 */
function LanguageSection() {
  const t = useT();
  const { locale, setLocale } = useLocale();

  return (
    <section className="fc-account__section">
      <h2>{t("Language")}</h2>
      <p className="fc-account__note">
        {t("The language this site is shown in.")}
      </p>

      <Field label={t("Language")}>
        <select
          className="fc-account__select"
          value={locale}
          onChange={(event) => setLocale(event.target.value as Locale)}
        >
          {LOCALES.map((option) => (
            <option key={option} value={option}>
              {LOCALE_NAMES[option]}
            </option>
          ))}
        </select>
      </Field>
    </section>
  );
}
function PasswordSection({ onSaved }: { onSaved: () => void }) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <section className="fc-account__section">
      <h2>{t("Password")}</h2>
      <p className="fc-account__note">
        Changing it re-seals the key that opens your photos, so it has to be
        done from a browser that can already open them.
      </p>
      <ChangePasswordForm
        busy={busy}
        error={error}
        setBusy={setBusy}
        setError={setError}
        onSaved={onSaved}
      />
    </section>
  );
}

function ChangePasswordForm({
  busy,
  error,
  setBusy,
  setError,
  onSaved,
}: {
  busy: boolean;
  error: string | null;
  setBusy: (value: boolean) => void;
  setError: (value: string | null) => void;
  onSaved: () => void;
}) {
  const t = useT();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      /*
       * The private key is re-sealed under the new password before anything is
       * sent, and both halves travel together. The server refuses a password
       * change on an account with a keypair that does not bring one, because
       * afterwards the old key cannot be opened by anybody and the photographs
       * are gone for good.
       *
       * Re-sealed, emphatically not regenerated: the keypair stays the same, so
       * every group key already wrapped to this person keeps working. The blob
       * has to be fetched because the key held in memory is non-extractable.
       */
      const { reSealKeyring } = await import("../../lib/keyring.js");
      const bundle = await trpcClient.auth.keyBundle.mutate({
        currentPassword,
      });

      const reSealed = bundle
        ? await reSealKeyring(bundle, currentPassword, newPassword)
        : undefined;

      await trpcClient.auth.changePassword.mutate({
        currentPassword,
        newPassword,
        ...(reSealed ? { reSealed } : {}),
      });
      setCurrentPassword("");
      setNewPassword("");
      onSaved();
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "That could not be changed.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      <Field label={t("Current password")} required>
        <Input
          type="password"
          value={currentPassword}
          autoComplete="current-password"
          disabled={busy}
          onChange={(event) => setCurrentPassword(event.target.value)}
        />
      </Field>

      <Field label={t("New password")} required error={error ?? undefined}>
        <Input
          type="password"
          value={newPassword}
          autoComplete="new-password"
          disabled={busy}
          onChange={(event) => setNewPassword(event.target.value)}
        />
      </Field>

      <Button
        type="submit"
        variant="primary"
        loading={busy}
        disabled={!currentPassword || newPassword.length < 8}
      >
        {t("Change password")}
      </Button>
    </form>
  );
}
